import { Gauge } from "lucide-react"
import { cn } from "@/lib/utils"
import type { MyUsage } from "@/services/usageService"

/** CreditMeter のプロパティ。 */
interface CreditMeterProps {
  /** 本人スコープの利用状況。null のとき何も表示しない。 */
  usage: MyUsage | null
  /** 紺地ヘッダー（AppHeader）上に表示する場合 true。文字色を白系に切り替える。既定 false。 */
  onDark?: boolean
}

/**
 * 「今日 X / Y クレジット」を常時表示する小型メーター。
 *
 * 上限接近で文字色が変化する（80% 以上=amber、100% 以上=red、それ未満=通常）。
 * limit=0（上限なし／viewer 等）のときは「—」で無害に表示する。
 * @param usage 本人スコープの利用状況（null なら非表示）。
 * @param onDark 紺地ヘッダー上に表示するか（既定 false）。
 * @returns ヘッダー常駐用のクレジット表示要素、または null。
 */
export function CreditMeter({ usage, onDark = false }: CreditMeterProps) {
  if (!usage) return null

  const { credits, limit } = usage.today
  const hasLimit = limit > 0
  const ratio = hasLimit ? credits / limit : 0

  // 上限接近に応じた文字色（80%=警告 amber、100%以上=危険 red）。
  // onDark（紺地ヘッダー）では白背景用の色ではなく白系の色を使う。
  const colorClass = !hasLimit
    ? onDark
      ? "text-white/75"
      : "text-muted-foreground"
    : ratio >= 1
      ? onDark
        ? "text-red-300"
        : "text-red-600"
      : ratio >= 0.8
        ? onDark
          ? "text-amber-300"
          : "text-amber-600"
        : onDark
          ? "text-white/75"
          : "text-muted-foreground"

  return (
    <div
      className={cn(
        "flex items-center gap-1.5 text-xs font-medium whitespace-nowrap",
        colorClass
      )}
      title="本日の利用クレジット"
    >
      <Gauge className="h-3.5 w-3.5 shrink-0" />
      <span>
        今日 {credits} / {hasLimit ? limit : "—"} クレジット
      </span>
    </div>
  )
}
